<template>
    <div class="space-y-2 pb-5">
        <div>
            <field
                v-slot="{ componentField, errorMessage }"
                name="routingNumber"
            >
                <div class="font-medium text-gray-800">Bank Routing Number</div>
                <input-text
                    v-bind="componentField"
                    class="w-full"
                    placeholder="Routing Number"
                    maxlength="50"
                    :disabled="isProcessing"
                    :pt="{
                        root: {
                            class: errorMessage
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="errorMessage"
                    class="text-red-600 pt-1 text-sm font-medium"
                >
                    {{ errorMessage }}
                </div>
            </field>
        </div>

        <div>
            <field
                v-slot="{ componentField, errorMessage }"
                name="accountNumber"
            >
                <div class="font-medium text-gray-800">Account Number</div>
                <input-text
                    v-bind="componentField"
                    class="w-full"
                    placeholder="Account Number"
                    :disabled="isProcessing"
                    maxlength="50"
                    :pt="{
                        root: {
                            class: errorMessage
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="errorMessage"
                    class="text-red-600 pt-1 text-sm font-medium"
                >
                    {{ errorMessage }}
                </div>
            </field>
        </div>

        <div>
            <field
                v-slot="{ componentField, errorMessage }"
                name="accountNumberConfirm"
                :validate-on-model-update="false"
            >
                <div class="font-medium text-gray-800">
                    Confirm Account Number
                </div>
                <input-text
                    v-bind="componentField"
                    class="w-full"
                    placeholder="Confirm Account Number"
                    :disabled="isProcessing"
                    maxlength="50"
                    :pt="{
                        root: {
                            class: errorMessage
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="errorMessage"
                    class="text-red-600 pt-1 text-sm font-medium"
                >
                    {{ errorMessage }}
                </div>
            </field>
        </div>

        <div>
            <field
                v-slot="{ componentField, errorMessage }"
                name="accountType"
            >
                <div class="font-medium text-gray-800">Account Type</div>
                <div class="flex flex-col gap-1 pt-1">
                    <div class="flex gap-2 pl-2">
                        <radio-button
                            v-bind="componentField"
                            input-id="checkingAccount"
                            value="CHECKING"
                            :disabled="isProcessing"
                            :pt="{
                                box: {
                                    class: errorMessage
                                        ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                        : ''
                                }
                            }"
                            :pt-options="{
                                mergeSections: true,
                                mergeProps: true
                            }"
                        />
                        <label
                            for="checkingAccount"
                            class="text-gray-600"
                            >Checking</label
                        >
                    </div>
                    <div class="flex gap-2 pl-2">
                        <radio-button
                            v-bind="componentField"
                            input-id="savingsAccount"
                            value="SAVINGS"
                            :disabled="isProcessing"
                            :pt="{
                                box: {
                                    class: errorMessage
                                        ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                        : ''
                                }
                            }"
                            :pt-options="{
                                mergeSections: true,
                                mergeProps: true
                            }"
                        />
                        <label
                            for="savingsAccount"
                            class="text-gray-600"
                            >Savings</label
                        >
                    </div>
                    <div
                        v-if="errorMessage"
                        class="text-red-600 text-sm font-medium"
                    >
                        {{ errorMessage }}
                    </div>
                </div>
            </field>
        </div>
    </div>
</template>

<script setup lang="ts">
import RadioButton from 'primevue/radiobutton'
import InputText from 'primevue/inputtext'
import { toTypedSchema } from '@vee-validate/yup'
import { mixed, object, string } from 'yup'
import { Field, useForm } from 'vee-validate'
import type { BankAccountPaymentMethod } from '~/components/Checkout/Sections/Payment/payment-method'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'

const emit = defineEmits<{
    (e: 'paymentMethod', payload: BankAccountPaymentMethod): void
}>()

const bankAccountSchema = toTypedSchema(
    object({
        routingNumber: string().required(
            'Please enter your bank routing number'
        ),
        accountNumber: string().required(
            'Please enter your bank account number'
        ),
        accountNumberConfirm: string()
            .required('Please confirm your bank account number')
            .test({
                name: 'ConfirmAccountNumber',
                exclusive: false,
                message: 'Account numbers do not match, please double check.',
                test: function (value, context) {
                    return value === context.parent.accountNumber
                }
            }),
        accountType: mixed()
            .oneOf(
                ['CHECKING', 'SAVINGS'],
                'Please select either checking or savings'
            )
            .required('Please select the type of bank account')
    })
)

const { isProcessing } = useCheckoutFormProcessing()

const isDev = import.meta.env.DEV
const { handleSubmit } = useForm({
    validationSchema: bankAccountSchema,
    initialValues: {
        routingNumber: isDev ? '036001808' : '',
        accountNumber: isDev ? '123456789' : '',
        accountNumberConfirm: isDev ? '123456789' : '',
        accountType: ''
    }
})

const onSubmit = handleSubmit(values => {
    emit('paymentMethod', {
        type: 'BankAccountPaymentMethod',
        routingNumber: values.routingNumber,
        accountNumber: values.accountNumber,
        accountNumberConfirm: values.accountNumberConfirm,
        accountType: values.accountType
    })
})

async function submitBankAccountForm() {
    await onSubmit()
}

defineExpose({
    submitBankAccountForm
})
</script>
